import { Identity } from './User'

export interface CrossClientQueryAtomicClause {
  variable: string
  description: string
  questionForm: string
}

export interface CrossClientQueryLogicalQuestionDecomposition {
  explanation: string
  logicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
}

export interface CrossClientQueryLogicalQuestionNNFDecomposition {
  nnfExplanation: string
  nnfLogicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
  negatedClauses: Record<string, string>
}

export const CrossClientQueryPhases = {
  NO_CLIENTS_WERE_FOUND: 'noClientsWereFound',
  NO_CLIENTS_AFTER_FILTER_IDS: 'noClientsAfterFilterIDs',
  NO_CLIENTS_AFTER_FILTER_ROLE: 'noClientsAfterFilterRole',
  QUESTION_ERROR: 'questionError',
  INITIALIZED: 'initialized',
  LOCAL_READY: 'localReady',
  LOCAL_QUEUED: 'localQueued',
  LOCAL_PROCESSING: 'localProcessing',
  AI_READY: 'aiReady',
  AI_QUEUED: 'aiQueued',
  AI_PROCESSING: 'aiProcessing',
  AI_STOPPED: 'aiStopped',
  CANCELING: 'canceling',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  ERRORED: 'errored',
} as const

export type CrossClientQueryPhase = (typeof CrossClientQueryPhases)[keyof typeof CrossClientQueryPhases]

export const CrossClientQueryResultStatuses = {
  INITIALIZED: 'initialized',
  LOCAL_RESOLVER_PROCESSING: 'localResolverProcessing',
  AI_PENDING: 'aiPending',
  AI_PROCESSING: 'aiProcessing',
  SUCCEEDED: 'succeeded',
  NO_DATA: 'noData',
  FAILED: 'failed',
} as const

export type CrossClientQueryResultStatus = (typeof CrossClientQueryResultStatuses)[keyof typeof CrossClientQueryResultStatuses]

export const CrossClientQueryResultMatches = {
  MATCH: 'full',
  PARTIAL: 'partial',
  NO_MATCH: 'noMatch',
} as const

export type CrossClientQueryResultMatch = (typeof CrossClientQueryResultMatches)[keyof typeof CrossClientQueryResultMatches]

export const CROSS_CLIENT_INCOMPLETE_TAB = 'incomplete'

export type CrossClientResultTab = CrossClientQueryResultMatch | typeof CROSS_CLIENT_INCOMPLETE_TAB

export const CrossClientResultTabs: CrossClientResultTab[] = [
  CrossClientQueryResultMatches.MATCH,
  CrossClientQueryResultMatches.PARTIAL,
  CROSS_CLIENT_INCOMPLETE_TAB,
  CrossClientQueryResultMatches.NO_MATCH,
]

export interface CrossClientQueryMatchDistribution {
  full: number
  partial: number
  noMatch: number
}

export const CrossClientQueryResultEvaluationTypes = {
  RESOLVER: 'resolver',
  AI: 'ai',
  LOCAL_RESOLVER: 'localResolver',
} as const

export type CrossClientQueryResultEvaluationType = (typeof CrossClientQueryResultEvaluationTypes)[keyof typeof CrossClientQueryResultEvaluationTypes]

export const CrossClientQueryAiEvaluationModes = {
  STEPPED: 'stepped',
  ALL: 'all',
} as const

export type CrossClientQueryAiEvaluationMode = (typeof CrossClientQueryAiEvaluationModes)[keyof typeof CrossClientQueryAiEvaluationModes]

export const CrossClientQueryStopReasons = {
  TOO_BROAD: 'tooBroad',
  TOO_NARROW: 'tooNarrow',
} as const

export type CrossClientQueryStopReason = (typeof CrossClientQueryStopReasons)[keyof typeof CrossClientQueryStopReasons]

export const CrossClientActionPhases = {
  STARTED: 'started',
  COMPLETED: 'completed',
} as const

export type CrossClientActionPhase = (typeof CrossClientActionPhases)[keyof typeof CrossClientActionPhases]

export interface CrossClientAction {
  id: string
  executionId: string
  clientId: string
  actionType: string
  phase: CrossClientActionPhase
  details: Record<string, unknown> | null
  startedAt: string
  finishedAt: string | null
}

export const CrossClientQueryClientQueryModes = {
  ONLY_OWNED: 'onlyOwned',
  WITH_SHARED: 'withShared',
  ALL_AVAILABLE: 'allAvailable',
} as const

export type CrossClientQueryClientQueryMode = (typeof CrossClientQueryClientQueryModes)[keyof typeof CrossClientQueryClientQueryModes]

interface ClientProfileCrossClientQueryDataSource {
  type: 'clientProfile'
}

interface IntegrationCrossClientQueryDataSource {
  type: 'integration'
  integrationType: string
}

interface MeetingCrossClientQueryDataSource {
  type: 'meeting'
}

export type CrossClientQueryDataSource = ClientProfileCrossClientQueryDataSource | IntegrationCrossClientQueryDataSource | MeetingCrossClientQueryDataSource

export interface CrossClientQueryExecution {
  id: string
  createdAt: string
  updatedAt: string
  finishedAt: string | null
  canceledAt: string | null
  bulkStartedAt: string | null
  bulkFinishedAt: string | null
  localStartedAt: string | null
  localFinishedAt: string | null
  aiStartedAt: string | null
  aiFinishedAt: string | null
  createdBy: Identity
  updatedBy: Identity
  phase: CrossClientQueryPhase
  clientIds: string[]
  resultCount: number
  sources: CrossClientQueryDataSource[]
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  resolvableClauseVariables?: string[] | null
  localResolvableClauseVariables?: string[] | null
  userQuestion: string
  clientQueryMode?: CrossClientQueryClientQueryMode
  aiEvaluationMode?: CrossClientQueryAiEvaluationMode | null
  aiBatchSize?: number | null
  stopReason?: CrossClientQueryStopReason | null
}

export const getExecutionStartedAt = (execution: CrossClientQueryExecution): string => {
  return execution.bulkStartedAt ?? execution.localStartedAt ?? execution.aiStartedAt ?? execution.createdAt
}

export interface CrossClientQueryResolverScore {
  resolverId: string
  score: number
  reasoning: string | null
  parameters: Record<string, unknown> | null
}

export interface CrossClientQueryPropositionResolverScores {
  variable: string
  resolverScores: CrossClientQueryResolverScore[]
}

export interface CrossClientQueryClauseResolverMapping {
  propositionResolverScores: CrossClientQueryPropositionResolverScores[]
}

export interface CrossClientQueryDecompositionResult {
  executionId: string
  phase: CrossClientQueryPhase
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping
  localClauseResolverMapping?: CrossClientQueryClauseResolverMapping
  resolvableClauseVariables: string[]
  localResolvableClauseVariables: string[]
}

export interface CrossClientQueryResultSummary {
  fullCount: number
  partialCount: number
  noMatchCount: number
  pendingCount: number
  totalCount: number
}

export type CrossClientQueryDecision = boolean | null

export interface CrossClientQueryResultSource {
  source: CrossClientQueryDataSource
  retrievedAt: string | null
  metadata?: Record<string, unknown>
}

export interface CrossClientQueryResultAnswer {
  decision: CrossClientQueryDecision
  explanation: string
  source?: CrossClientQueryResultSource
}

export interface CrossClientQueryResult {
  id: string
  clientId: string
  status: CrossClientQueryResultStatus
  createdAt: string
  updatedAt: string
  answers?: CrossClientQueryResultAnswer[]
  match?: CrossClientQueryResultMatch
  evaluationType?: CrossClientQueryResultEvaluationType
}

export interface EvaluatedCrossClientQueryResult extends CrossClientQueryResult {
  match: CrossClientQueryResultMatch
}

export interface CrossClientQueryExecutionProgressResponse {
  executionId: string
  phase: CrossClientQueryPhase
  status: CrossClientQueryResultStatus
  resultCount: number
  createdAt: string
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}

export interface CrossClientQueryExecutionProgress {
  executionId: string
  phase: CrossClientQueryPhase
  statuses: Record<CrossClientQueryResultStatus, number>
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}
